import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Grid,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Description,
} from '@mui/icons-material';
import { excelMappingApi } from '../api/excelMapping';
import type { ExcelMappingConfig } from '../api/excelMapping';
import { useTranslation } from 'react-i18next';

interface ExcelMappingConfigListProps {
  onEdit: (config: ExcelMappingConfig) => void;
  onView: (config: ExcelMappingConfig) => void;
  onCreate: () => void;
}

const ExcelMappingConfigList: React.FC<ExcelMappingConfigListProps> = ({
  onEdit,
  onView,
  onCreate,
}) => {
  const [configs, setConfigs] = useState<ExcelMappingConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    config: ExcelMappingConfig | null;
  }>({ open: false, config: null });
  const { t } = useTranslation();

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const response = await excelMappingApi.getConfigs();
      if (response.success && response.data) {
        setConfigs(Array.isArray(response.data) ? response.data : [response.data]);
      } else {
        setError(response.message || t('excelMapping.errors.loadConfigs', 'Error al cargar las configuraciones'));
      }
    } catch (err) {
      setError(t('excelMapping.errors.loadConfigs', 'Error al cargar las configuraciones'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.config) return;

    try {
      const response = await excelMappingApi.deleteConfig(deleteDialog.config.id!);
      if (response.success) {
        setConfigs(configs.filter(c => c.id !== deleteDialog.config!.id));
        setDeleteDialog({ open: false, config: null });
      } else {
        setError(response.message || 'Error al eliminar la configuración');
      }
    } catch (err) {
      setError('Error al eliminar la configuración');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <Typography>{t('excelMapping.loading', 'Cargando configuraciones...')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('excelMapping.title', 'Configuraciones de Mapeo Excel')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={onCreate}
        >
          {t('excelMapping.newConfig', 'Nueva Configuración')}
        </Button>
      </Box>

      {configs.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <Description sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('excelMapping.emptyTitle', 'No hay configuraciones creadas')}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                {t('excelMapping.emptyDesc', 'Crea tu primera configuración de mapeo para comenzar a procesar archivos Excel')}
              </Typography>
              <Button variant="contained" startIcon={<Add />} onClick={onCreate}>
                {t('excelMapping.createConfig', 'Crear Configuración')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {configs.map((config) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={config.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" component="h2" noWrap>
                      {config.name}
                    </Typography>
                    <Chip
                      label={config.isActive ? t('excelMapping.active', 'Activa') : t('excelMapping.inactive', 'Inactiva')}
                      color={config.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>

                  {config.description && (
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {config.description}
                    </Typography>
                  )}

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <strong>{t('excelMapping.file', 'Archivo')}:</strong> {config.excelFileName}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" mb={2}>
                    <strong>{t('excelMapping.mappedFields', 'Campos mapeados')}:</strong> {config.fields?.length || 0}
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    {t('common.created', 'Creado')}: {new Date(config.createdAt || '').toLocaleDateString()}
                  </Typography>
                </CardContent>

                <Box p={2} pt={0}>
                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => onView(config)}
                    >
                      {t('common.view', 'Ver')}
                    </Button>
                    <Button
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => onEdit(config)}
                    >
                      {t('common.edit', 'Editar')}
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteDialog({ open: true, config })}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog de confirmación de eliminación */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, config: null })}
      >
        <DialogTitle>{t('common.confirmDelete', 'Confirmar eliminación')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('excelMapping.confirmDeleteText', '¿Estás seguro de que quieres eliminar la configuración')} "{deleteDialog.config?.name}"? {t('common.cannotUndo', 'Esta acción no se puede deshacer.')} 
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, config: null })}>
            {t('common.cancel', 'Cancelar')}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('common.delete', 'Eliminar')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExcelMappingConfigList;
